import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { RadioGroup, Modal, Button } from '@ets/design-system';

import { LANGUAGES } from 'Intl/Intl';
import { useDispatch, useSelector } from '_common/hooks';

import { closeModal } from '_common/modals/ModalsSlice';
import { useChangeLanguageMutation } from 'Settings/pages/UserSettingsPage/UserSettingsApi';

const MODAL = 'LanguageSettingModal';

const LanguageSettingModal = () => {
  const dispatch = useDispatch();

  const isOpen = useSelector((state) => state.modals.open[MODAL]);
  const locale = useSelector((state) => state.intl.locale);
  const typedLocale = useMemo<Language | undefined>(() => {
    const parsedLocale = locale in LANGUAGES ? locale : locale.split('-')[0];
    return LANGUAGES[parsedLocale as Language['code']] ?? undefined;
  }, [locale]);

  const [changeLanguage] = useChangeLanguageMutation();

  const [selectedLanguage, setSelectedLanguage] = useState<Language>();

  useEffect(() => {
    if (isOpen) {
      setSelectedLanguage(typedLocale);
    }
  }, [isOpen, typedLocale]);

  const handleChangeLanguage = () => {
    if (selectedLanguage && typedLocale?.code !== selectedLanguage.code) {
      changeLanguage({ language: selectedLanguage.code });
    }
    close();
  };

  const close = () => {
    dispatch(closeModal(MODAL));
  };

  return (
    <Modal width="75rem" open={!!isOpen} close={close} testId="change-language">
      <Modal.Header>
        <FormattedMessage id="settings.languages.header" />
      </Modal.Header>
      <Modal.Body>
        <RadioGroup
          value={selectedLanguage?.code}
          onChange={(e) => setSelectedLanguage(LANGUAGES[e.target.value])}
          items={Object.values(LANGUAGES).map((language: Language) => ({
            label: language.name,
            value: language.code,
          }))}
          direction="vertical"
          size="xs"
          testId="language"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button size="m" variant="standard" onClick={close} testId="language-modal-close">
          <FormattedMessage id="global.close" />
        </Button>
        <Button
          size="m"
          variant="primary"
          style={{ margin: '0 0 0 1rem' }}
          onClick={handleChangeLanguage}
          testId="language-modal-submit"
        >
          <FormattedMessage id="UPDATE_LANGUAGE" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LanguageSettingModal;
